import { useEffect, useState } from 'react'
import { Bell, Download } from 'lucide-react'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useAuthStore } from '@/core/auth/auth-store'
import { enablePush, pushPermission, pushSupported } from '@/core/firebase/push'
import { isIOS, isInstalled } from '@/shared/hooks/use-install'
import { InstallSheet } from '@/shared/components/install-sheet'
import { showToast } from '@/shared/ui/toast'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'

const DISMISS_KEY = 'um-push-nudge-dismissed'

/**
 * In-context permission ask (release R1) — shown ONLY after the player did
 * something notifications serve (joined/created a match), never on load
 * (premature permission prompts are a reject-on-sight anti-pattern).
 * "Ahora no" dismisses persistently; Ajustes keeps the switch.
 */
export function PushNudge() {
  const user = useAuthStore((s) => s.user)
  const [visible, setVisible] = useState(false)
  // iOS in the browser can't push until the PWA is INSTALLED — silently
  // hiding the nudge there buried the whole feature (real-device test,
  // 2026-07-05). Show the install-first variant instead.
  const [needsInstall, setNeedsInstall] = useState(false)
  const [installSheet, setInstallSheet] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (AUTH_BYPASS || !user || user.isAnonymous) return
    if (localStorage.getItem(DISMISS_KEY)) return
    if (pushPermission() !== 'default' && pushPermission() !== 'unsupported') return
    let cancelled = false
    void pushSupported().then((ok) => {
      if (cancelled) return
      if (ok) setVisible(true)
      else if (isIOS() && !isInstalled()) {
        setNeedsInstall(true)
        setVisible(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [user])

  if (!visible) return null

  async function onEnable() {
    if (!user || busy) return
    setBusy(true)
    const result = await enablePush(user.uid)
    setBusy(false)
    setVisible(false)
    if (result === 'granted') showToast('Avisos activados — te avisamos de uniones y cancelaciones')
    else if (result === 'denied') showToast('Avisos bloqueados en el navegador — puedes activarlos en Ajustes del sitio')
  }

  function onDismiss() {
    localStorage.setItem(DISMISS_KEY, '1')
    setVisible(false)
  }

  return (
    <>
      <Card className="um-enter">
        <div className="p-4 flex flex-col gap-3">
          <p className="flex items-start gap-3 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
            <Bell size={17} className="shrink-0 mt-0.5 text-[color:var(--accent)]" />
            {needsInstall
              ? 'Instala la app y te avisamos si alguien se une, deja el partido o se cancela.'
              : 'Activa los avisos y te avisamos si alguien se une, deja el partido o se cancela.'}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {needsInstall ? (
              <Button disabled={busy} onClick={() => setInstallSheet(true)}>
                <Download size={16} /> Instalar
              </Button>
            ) : (
              <Button disabled={busy} onClick={() => void onEnable()}>
                <Bell size={16} /> Activar
              </Button>
            )}
            <Button variant="outline" disabled={busy} onClick={onDismiss}>
              Ahora no
            </Button>
          </div>
        </div>
      </Card>
      <InstallSheet open={installSheet} onClose={() => setInstallSheet(false)} />
    </>
  )
}
